import React, { useState, useEffect, useCallback } from 'react';
import { formatCurrency } from '../utils/dateUtils.ts';
import {
  getAssets,
  getPortfolio,
  createAsset,
  updateAsset,
  deleteAsset,
  refreshQuotes,
  getAssetPVP,
  lookupCNPJ,
  FIIAsset,
  FIIAssetPortfolioItem,
  FIIPortfolioResponse,
  CreateFIIAssetRequest,
} from '../services/fiiApi.ts';
import './FIIPortfolio.css';

const VALID_SEGMENTS = [
  'Shoppings', 'Papel', 'Renda Urbana', 'Logística', 'Híbrido',
  'Lajes Corporativas', 'Agro', 'Hospital', 'Hotel', 'Educacional',
];

const FIIPortfolio: React.FC = () => {
  const [portfolio, setPortfolio] = useState<FIIPortfolioResponse | null>(null);
  const [assets, setAssets] = useState<FIIAsset[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');
  const [warning, setWarning] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingAsset, setEditingAsset] = useState<FIIAsset | null>(null);

  const [form, setForm] = useState<CreateFIIAssetRequest>({
    ticker: '',
    segment: '',
    objective: '',
    target_allocation_pct: 0,
    ceiling_price: null,
    use_pvp: false,
    cnpj: '',
    fund_name: '',
  });
  const [pvpValue, setPvpValue] = useState<number | null>(null);
  const [pvpLoading, setPvpLoading] = useState(false);
  const [pvpError, setPvpError] = useState('');
  const [cnpjLoading, setCnpjLoading] = useState(false);
  const [cnpjError, setCnpjError] = useState('');
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState<number | null>(null);

  const loadPortfolio = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [portfolioData, assetsData] = await Promise.all([
        getPortfolio(),
        getAssets(),
      ]);
      setPortfolio(portfolioData);
      setAssets(assetsData);
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Erro ao carregar carteira');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadPortfolio();
  }, [loadPortfolio]);

  const handleRefreshQuotes = async () => {
    setRefreshing(true);
    try {
      await refreshQuotes();
      await loadPortfolio();
    } catch (err: any) {
      setError('Erro ao atualizar cotações');
    } finally {
      setRefreshing(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    setWarning('');
    try {
      if (editingAsset) {
        const result = await updateAsset(editingAsset.id, {
          segment: form.segment,
          objective: form.objective,
          target_allocation_pct: form.target_allocation_pct,
          ceiling_price: form.use_pvp ? null : form.ceiling_price,
          use_pvp: form.use_pvp,
          cnpj: form.cnpj || '',
          fund_name: form.fund_name || '',
        });
        if (result.warning) setWarning(result.warning);
      } else {
        const result = await createAsset({
          ...form,
          ceiling_price: form.use_pvp ? null : form.ceiling_price,
        });
        if (result.warning) setWarning(result.warning);
      }
      setShowAddForm(false);
      setEditingAsset(null);
      resetForm();
      await loadPortfolio();
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Erro ao salvar ativo');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (asset: FIIAsset) => {
    if (!window.confirm(`Confirma exclusão de ${asset.ticker}?`)) return;
    setDeletingId(asset.id);
    try {
      await deleteAsset(asset.id);
      await loadPortfolio();
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Erro ao excluir ativo');
    } finally {
      setDeletingId(null);
    }
  };

  const startEdit = (asset: FIIAsset) => {
    setEditingAsset(asset);
    setForm({
      ticker: asset.ticker,
      segment: asset.segment,
      objective: asset.objective,
      target_allocation_pct: asset.target_allocation_pct,
      ceiling_price: asset.ceiling_price,
      use_pvp: asset.use_pvp,
      cnpj: asset.cnpj ? formatCNPJMask(asset.cnpj) : '',
      fund_name: asset.fund_name || '',
    });
    setPvpValue(null);
    setPvpError('');
    setCnpjError('');
    if (asset.use_pvp) {
      fetchPVP(asset.ticker);
    }
    setShowAddForm(true);
  };

  const resetForm = () => {
    setForm({ ticker: '', segment: '', objective: '', target_allocation_pct: 0, ceiling_price: null, use_pvp: false, cnpj: '', fund_name: '' });
    setEditingAsset(null);
    setPvpValue(null);
    setPvpError('');
    setCnpjError('');
  };

  const formatCNPJMask = (value: string): string => {
    const digits = value.replace(/\D/g, '').slice(0, 14);
    return digits
      .replace(/^(\d{2})(\d)/, '$1.$2')
      .replace(/^(\d{2})\.(\d{3})(\d)/, '$1.$2.$3')
      .replace(/\.(\d{3})(\d)/, '.$1/$2')
      .replace(/(\d{4})(\d)/, '$1-$2');
  };

  const cnpjDigitsOnly = (v: string) => v.replace(/\D/g, '');

  const handleCNPJChange = (raw: string) => {
    const masked = formatCNPJMask(raw);
    setForm(f => ({ ...f, cnpj: masked }));
    setCnpjError('');
    const digits = cnpjDigitsOnly(masked);
    if (digits.length === 14) {
      fetchFundName(digits);
    }
  };

  const fetchFundName = async (cnpj: string) => {
    setCnpjLoading(true);
    setCnpjError('');
    try {
      const data = await lookupCNPJ(cnpj);
      setForm(f => ({ ...f, fund_name: data.fund_name }));
    } catch (err: any) {
      setCnpjError(err?.response?.data?.error || 'CNPJ não encontrado');
    } finally {
      setCnpjLoading(false);
    }
  };

  const fetchPVP = async (ticker: string) => {
    if (!ticker || ticker.length < 5) return;
    setPvpLoading(true);
    setPvpError('');
    setPvpValue(null);
    try {
      const data = await getAssetPVP(ticker);
      setPvpValue(data.book_value);
    } catch (err: any) {
      setPvpError(err?.response?.data?.error || 'P/VP não disponível para este ativo');
    } finally {
      setPvpLoading(false);
    }
  };

  const handleTogglePVP = (checked: boolean) => {
    setForm(f => ({ ...f, use_pvp: checked }));
    if (checked) {
      const ticker = form.ticker || editingAsset?.ticker;
      if (ticker) fetchPVP(ticker);
    } else {
      setPvpValue(null);
      setPvpError('');
    }
  };

  const formatPct = (v: number | null | undefined, decimals = 2) => {
    if (v == null) return 'N/A';
    const s = v.toFixed(decimals);
    return `${v >= 0 ? '+' : ''}${s}%`;
  };

  const pctClass = (v: number | null | undefined) => {
    if (v == null) return '';
    return v >= 0 ? 'positive' : 'negative';
  };

  const getActionClass = (action: string | null | undefined) => {
    if (!action) return '';
    if (action === 'Aportar') return 'action-buy';
    if (action === 'Resgatar') return 'action-sell';
    return 'action-hold';
  };

  return (
    <div className="fii-portfolio">
      <div className="page-header">
        <div>
          <h1>🏢 Carteira FII</h1>
          <p>Fundos de Investimento Imobiliário</p>
        </div>
        <div className="header-actions">
          <button
            className="secondary-button"
            onClick={handleRefreshQuotes}
            disabled={refreshing}
          >
            {refreshing ? 'Atualizando...' : '🔄 Atualizar Cotações'}
          </button>
          <button
            className="primary-button"
            onClick={() => { resetForm(); setShowAddForm(true); }}
          >
            + Adicionar FII
          </button>
        </div>
      </div>

      {error && <div className="alert alert-error">{error}</div>}
      {warning && <div className="alert alert-warning">{warning}</div>}

      {/* Portfolio Summary Cards */}
      {portfolio && (
        <div className="summary-cards">
          <div className="summary-card">
            <span className="card-label">Total Investido</span>
            <span className="card-value">{formatCurrency(portfolio.total_invested)}</span>
          </div>
          <div className="summary-card">
            <span className="card-label">Valor Atual</span>
            <span className="card-value">{formatCurrency(portfolio.total_current_value)}</span>
          </div>
          <div className="summary-card">
            <span className="card-label">Rentabilidade</span>
            <span className={`card-value ${pctClass(portfolio.total_profitability_pct)}`}>
              {formatCurrency(portfolio.total_profitability_rs)} ({formatPct(portfolio.total_profitability_pct)})
            </span>
          </div>
          <div className="summary-card">
            <span className="card-label">Rendimentos Totais</span>
            <span className="card-value positive">{formatCurrency(portfolio.total_income)}</span>
          </div>
          <div className="summary-card">
            <span className="card-label">Retorno Total</span>
            <span className={`card-value ${pctClass(portfolio.total_return_pct)}`}>
              {formatPct(portfolio.total_return_pct)}
            </span>
          </div>
          {portfolio.quotes_stale && (
            <div className="stale-badge">
              ⚠️ Cotações desatualizadas — última atualização: {portfolio.quotes_updated_at || 'N/D'}
            </div>
          )}
        </div>
      )}

      {/* Add/Edit Form */}
      {showAddForm && (
        <div className="form-container">
          <h3>{editingAsset ? `Editar ${editingAsset.ticker}` : 'Adicionar FII'}</h3>
          <form onSubmit={handleSubmit}>
            <div className="form-row">
              <div className="form-group">
                <label>Ticker</label>
                <input
                  type="text"
                  value={form.ticker}
                  onChange={e => setForm(f => ({ ...f, ticker: e.target.value.toUpperCase() }))}
                  placeholder="Ex: HGLG11"
                  maxLength={7}
                  disabled={!!editingAsset}
                  required
                />
              </div>
              <div className="form-group">
                <label>Segmento</label>
                <select
                  value={form.segment}
                  onChange={e => setForm(f => ({ ...f, segment: e.target.value }))}
                  required
                >
                  <option value="">Selecione...</option>
                  {VALID_SEGMENTS.map(s => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Objetivo</label>
                <input
                  type="text"
                  value={form.objective}
                  onChange={e => setForm(f => ({ ...f, objective: e.target.value }))}
                  placeholder="Ex: Renda passiva"
                />
              </div>
              <div className="form-group">
                <label>Alocação Alvo (%)</label>
                <input
                  type="number"
                  min={0}
                  max={100}
                  step={0.01}
                  value={form.target_allocation_pct}
                  onChange={e => setForm(f => ({ ...f, target_allocation_pct: parseFloat(e.target.value) || 0 }))}
                />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Preço Teto (R$)</label>
                <div className="ceiling-price-row">
                  <input
                    type="number"
                    min={0}
                    step={0.01}
                    value={form.use_pvp ? (pvpValue ?? '') : (form.ceiling_price ?? '')}
                    onChange={e => {
                      const v = e.target.value;
                      setForm(f => ({ ...f, ceiling_price: v === '' ? null : parseFloat(v) }));
                    }}
                    placeholder={form.use_pvp ? (pvpLoading ? 'Buscando VPA...' : 'VPA automático') : 'Ex: 120.00'}
                    disabled={form.use_pvp}
                    className={form.use_pvp ? 'input-disabled' : ''}
                  />
                </div>
                {form.use_pvp && pvpError && (
                  <span className="field-error">{pvpError}</span>
                )}
                {form.use_pvp && pvpValue != null && (
                  <span className="field-hint">VPA: R$ {pvpValue.toFixed(2)} (Brapi)</span>
                )}
              </div>
              <div className="form-group pvp-toggle-group">
                <label>Usa P/VP</label>
                <label className="toggle-switch">
                  <input
                    type="checkbox"
                    checked={form.use_pvp || false}
                    onChange={e => handleTogglePVP(e.target.checked)}
                  />
                  <span className="toggle-slider"></span>
                </label>
                <span className="toggle-label-text">
                  {form.use_pvp ? 'Preço teto = VPA' : 'Manual'}
                </span>
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>CNPJ do Fundo</label>
                <input
                  type="text"
                  value={form.cnpj || ''}
                  onChange={e => handleCNPJChange(e.target.value)}
                  placeholder="XX.XXX.XXX/XXXX-XX"
                  maxLength={18}
                />
                {cnpjLoading && <span className="field-hint">Buscando nome do fundo...</span>}
                {cnpjError && <span className="field-error">{cnpjError}</span>}
              </div>
              <div className="form-group">
                <label>Nome do Fundo</label>
                <input
                  type="text"
                  value={form.fund_name || ''}
                  onChange={e => setForm(f => ({ ...f, fund_name: e.target.value }))}
                  placeholder="Preenchido automaticamente via CNPJ"
                />
              </div>
            </div>
            <div className="form-actions">
              <button type="submit" className="primary-button" disabled={saving}>
                {saving ? 'Salvando...' : (editingAsset ? 'Salvar' : 'Adicionar')}
              </button>
              <button
                type="button"
                className="secondary-button"
                onClick={() => { setShowAddForm(false); resetForm(); }}
                disabled={saving}
              >
                Cancelar
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Portfolio Table */}
      {loading ? (
        <div className="loading">Carregando carteira...</div>
      ) : portfolio && portfolio.assets.length > 0 ? (
        <div className="table-container">
          <table className="portfolio-table">
            <thead>
              <tr>
                <th>Ticker</th>
                <th>Qtd</th>
                <th>P. Médio</th>
                <th>Custo Total</th>
                <th>Cotação</th>
                <th>Valor Atual</th>
                <th>Rent. R$</th>
                <th>Rendimentos</th>
                <th>Desconto</th>
                <th>Aloc. Atual</th>
                <th>Ação</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {[...portfolio.assets]
                .sort((a, b) => b.quantity - a.quantity)
                .map((item: FIIAssetPortfolioItem) => (
                <tr key={item.id} className={item.quantity === 0 ? 'zero-quantity' : ''}>
                  <td className="ticker-cell">
                    <span className="ticker-badge" data-tooltip={item.segment}>
                      {item.ticker}
                    </span>
                    {item.quote_stale && <span className="stale-dot" title="Cotação desatualizada">●</span>}
                  </td>
                  <td className="number-cell">{item.quantity}</td>
                  <td className="number-cell">{formatCurrency(item.average_price)}</td>
                  <td className="number-cell">{formatCurrency(item.total_cost)}</td>
                  <td className="number-cell">
                    {item.current_quote > 0 ? formatCurrency(item.current_quote) : '—'}
                  </td>
                  <td className="number-cell">{formatCurrency(item.current_value)}</td>
                  <td
                    className={`number-cell ${pctClass(item.profitability_rs)}`}
                    title={item.profitability_pct != null ? formatPct(item.profitability_pct) : ''}
                  >
                    {item.profitability_rs != null ? formatCurrency(item.profitability_rs) : 'N/A'}
                  </td>
                  <td className="number-cell positive">
                    {item.total_income != null ? formatCurrency(item.total_income) : '—'}
                  </td>
                  <td className={`number-cell ${pctClass(item.discount_pct)}`}>
                    {formatPct(item.discount_pct)}
                  </td>
                  <td className="number-cell">
                    {item.current_allocation_pct != null ? `${item.current_allocation_pct.toFixed(1)}%` : '—'}
                  </td>
                  <td>
                    {item.action ? (
                      <span className={`action-badge ${getActionClass(item.action)}`}>
                        {item.action}
                      </span>
                    ) : '—'}
                  </td>
                  <td className="actions-cell">
                    <button
                      className="action-icon-button"
                      title="Editar"
                      onClick={() => {
                        const a = assets.find(a => a.id === item.id);
                        if (a) startEdit(a);
                      }}
                    >✏️</button>
                    <button
                      className="action-icon-button danger"
                      title="Excluir"
                      disabled={deletingId === item.id}
                      onClick={() => {
                        const a = assets.find(a => a.id === item.id);
                        if (a) handleDelete(a);
                      }}
                    >{deletingId === item.id ? '...' : '🗑️'}</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="empty-state">
          <span className="placeholder-icon">🏢</span>
          <p>Nenhum FII cadastrado. Clique em <strong>+ Adicionar FII</strong> para começar.</p>
        </div>
      )}
    </div>
  );
};

export default FIIPortfolio;
